import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ticketsApi } from "../utils/api";

export default function BookingSuccessPage() {
  const { ref } = useParams();
  const [order, setOrder]   = useState(null);
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    if (!ref) return;
    const load = () => {
      ticketsApi.getOrder(ref).then(d => {
        setOrder(d.order);
        setTickets(d.tickets || []);
      }).catch(() => {});
    };
    load();
    const poll = setInterval(() => {
      ticketsApi.getOrder(ref).then(d => {
        setOrder(d.order);
        setTickets(d.tickets || []);
        if (d.order.status === "confirmed") clearInterval(poll);
      }).catch(() => clearInterval(poll));
    }, 3000);
    return () => clearInterval(poll);
  }, [ref]);

  const downloadQR = (uid, name) => {
    const link = document.createElement("a");
    link.href = `/uploads/tickets/qr/${uid}.png`;
    link.download = `ticket-${name}-${uid.slice(0,8)}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const confirmed = order?.status === "confirmed";

  return (
    <div className="min-h-screen pt-24 pb-12 px-4" style={{background:"#0a0a0f"}}>
      <div className="max-w-lg mx-auto">

        {/* Status header */}
        <div className="text-center mb-8">
          {!order && <div className="w-12 h-12 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"/>}
          {order && (
            <>
              <div className={`w-20 h-20 rounded-full flex items-center justify-center text-3xl mx-auto mb-4 ${confirmed?"bg-green-500/20 border border-green-500/40":"bg-yellow-500/20 border border-yellow-500/40"}`}>
                {confirmed ? "✅" : "⏳"}
              </div>
              <h1 className="font-display text-3xl font-bold text-white mb-2">
                {confirmed ? "Booking Confirmed!" : "Processing Payment..."}
              </h1>
              <p className="text-gray-400 mb-1">{order.event_title}</p>
              <p className="text-xs text-brand-300 font-mono">{order.order_ref}</p>
            </>
          )}
        </div>

        {/* QR Tickets */}
        {confirmed && tickets.length > 0 && (
          <div className="mb-6">
            <h2 className="font-display text-xl font-bold text-white mb-4 text-center">Your QR Tickets</h2>
            <div className="space-y-4">
              {tickets.map((t, i) => (
                <div key={t.ticket_uid} className="glass rounded-2xl overflow-hidden">
                  {/* Ticket header */}
                  <div className="p-5" style={{background:"linear-gradient(135deg,rgba(124,34,200,0.3),rgba(155,63,232,0.1))"}}>
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-xs text-gray-400 mb-1">Ticket {i+1} of {tickets.length}</p>
                        <p className="font-bold text-white text-lg">{t.attendee_name}</p>
                        <p className="text-brand-300 text-sm">{order.tier_name} · {order.event_title}</p>
                        <p className="text-gray-400 text-xs mt-1">{order.event_date?.slice(0,10)} · {order.venue_name}</p>
                      </div>
                      {/* QR Code */}
                      <div className="flex-shrink-0 text-center">
                        <div className="bg-white p-2 rounded-xl inline-block">
                          <img
                            src={`/uploads/tickets/qr/${t.ticket_uid}.png`}
                            alt="QR Code"
                            width="100"
                            height="100"
                            className="block"
                            onError={e => { e.target.style.display="none"; }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Scan at entrance</p>
                      </div>
                    </div>
                  </div>

                  {/* Ticket footer */}
                  <div className="px-5 py-3 border-t border-white/10 flex items-center justify-between">
                    <p className="text-xs font-mono text-gray-600">{t.ticket_uid.slice(0,18)}...</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => downloadQR(t.ticket_uid, t.attendee_name)}
                        className="text-xs bg-brand-700 hover:bg-brand-600 text-white px-3 py-1.5 rounded-lg transition flex items-center gap-1"
                      >
                        ⬇ Download QR
                      </button>
                      <a href={`/api/tickets/ticket/${t.ticket_uid}/pdf`}
                        download={`ticket-${t.attendee_name}.pdf`}
                        className="text-xs glass text-gray-300 hover:text-white px-3 py-1.5 rounded-lg transition flex items-center gap-1"
                      >
                        📄 PDF
                      </a>
                      <button
                        onClick={() => window.open(`/verify/${t.ticket_uid}`, '_blank')}
                        className="text-xs glass text-gray-300 hover:text-white px-3 py-1.5 rounded-lg transition"
                      >
                        Verify
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Info box */}
        {confirmed && (
          <div className="glass rounded-2xl p-5 mb-6">
            <div className="space-y-3">
              {[
                {i:"📧", t:"Email confirmation", d:"Sent to your email address"},
                {i:"📱", t:"WhatsApp QR codes",  d:"Sent to your WhatsApp number"},
                {i:"🚪", t:"At the venue",        d:"Show QR code at entrance for instant check-in"},
              ].map(x=>(
                <div key={x.t} className="flex gap-3">
                  <span className="text-xl">{x.i}</span>
                  <div><p className="text-white text-sm font-medium">{x.t}</p><p className="text-gray-500 text-xs">{x.d}</p></div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <Link to="/" className="flex-1 py-3 glass rounded-xl text-gray-300 text-sm font-medium text-center transition hover:text-white">
            Browse Events
          </Link>
          <Link to="/my-tickets" className="flex-1 py-3 bg-brand-700 hover:bg-brand-600 rounded-xl text-white text-sm font-semibold text-center transition">
            My Tickets
          </Link>
        </div>

      </div>
    </div>
  );
}
